<script lang="ts">
import { defineComponent, PropType, ref, computed, watch, onMounted } from 'vue'
import InputCustomFieldItem from '@/components/customFiled/InputCustomFieldItem.vue'
import type { Ref } from 'vue'

export default defineComponent({
  name: 'InputCustomFieldItems',

  components: {
    InputCustomFieldItem,
  },

  props: {
    /**
     * バリデーションの名前空間
     * @description 自身に紐づく名前空間のバリデーションコールにのみ反応する。
     * @description 値は任意であるが、store に登録されるバリデーションキューの namespace の値と同一にする必要がある。
     * @description Form submit の場合、form の name 属性の値にする。
     */
    namespace: {
      type: String,
      default: '',
    },

    customFieldConfigItems: {
      type: Array as PropType<CustomFieldConfig[]>,
      required: true,
    },

    inputCustomFields: {
      type: Object as PropType<Inputs>,
      required: true,
    },
  },

  emits: ['update:customFields'],

  setup(props, { emit }) {
    const localInputs: Ref<Inputs> = ref({})

    /**
     * カスタムフィールドがあるかどうか
     */
    const hasCustomFields = computed<boolean>(() => {
      return !!Object.keys(props.customFieldConfigItems).length
    })

    /**
     * カスタムフィールドの情報データとカスタムフィールドの入力データの一致性チェック
     * @description カスタムフィールドの入力データオブジェクトの key の値が、カスタムフィールドの情報データ配列内のいずれかのデータの「id」の値が一致するものが存在しているかどうか
     */
    const isValidCustomFields = computed<boolean>(() => {
      return Object.keys(props.customFieldConfigItems).length === Object.keys(localInputs.value).length && props.customFieldConfigItems.every(v => {
        return Object.keys(localInputs.value).some(key => key === v.id.toString())
      })
    })

    watch(() => props.inputCustomFields, () => {
      Object.assign(localInputs.value, props.inputCustomFields)
    }, { deep: true })

    onMounted(() => {
      Object.assign(localInputs.value, props.inputCustomFields)
    })

    /**
     * カスタムフィールドの入力時に、子コンポーネントから emit されたデータを詰め直して親に emit する
     * @param key 入力のあったカスタムフィールドの key 名
     * @param value 入力のあったカスタムフィールドの value
     */
    const updateCustomFieldValue = (key: string, value: InputValue): void => {
      const _key = Object.keys(localInputs.value).find(v => v === key)
      if (_key) {
        localInputs.value[_key].value = value
        emit('update:customFields', localInputs.value)
      }
    }

    return {
      // data
      localInputs,

      // computed
      hasCustomFields,
      isValidCustomFields,

      // methods
      updateCustomFieldValue,
    }
  }
})
</script>

<template>
  <div
    v-if="hasCustomFields && isValidCustomFields"
    class="l-rows">
    <div
      v-for="item in customFieldConfigItems"
      :key="item.name"
      class="l-rows--row">
      <InputCustomFieldItem
        :model-value="localInputs[item.id].value"
        :field-id="item.id"
        :header="item.title"
        :field-type="item.fieldType"
        :namespace="namespace"
        :name="localInputs[item.id].name"
        :id-attr-name="`${item.name}[custom_field_id]`"
        :select-items="item.selectItems"
        :validation-scheme="localInputs[item.id].validationScheme"
        @update:model-value="updateCustomFieldValue(item.id.toString(), $event)" />
    <!-- /.l-rows row --></div>
  <!-- /.l-rows --></div>
</template>
